import { lazy } from 'react';

import type { RouteConfig } from '../types';
import { alertRoutes } from './groups/alerts';
import { clientDashboardRoutes } from './groups/clientDashboard';
import { clientRoutes } from './groups/clients';
import { companyRoutes } from './groups/companies';
import { copRoutes } from './groups/cop';
import { damageRoutes } from './groups/damages';
// Import route groups
import {
  activitiesRoutes,
  checkInRoutes,
  dashboardRoutes,
  gigsRoutes,
  importRoutes,
  jobSpecForecastRoutes,
  notificationsRoutes,
  readinessForecastRoutes,
  restorationsRoutes,
  revenueReportRoutes,
  sessionsRoutes,
} from './groups/dashboard';
import { divisionRoutes } from './groups/divisions';
import { emergencyRoutes } from './groups/emergency';
import { forecastsPLRoutes } from './groups/forecastsPL';
import { gpmDriveRoutes } from './groups/gpmDrive';
import { hierarchyRoutes } from './groups/hierarchy';
import { hrbsRoutes } from './groups/hrbs';
import { invoiceRoutes } from './groups/invoices';
import { licenseRoutes } from './groups/licenses';
import { marketRoutes } from './groups/markets';
import { perDiemRoutes } from './groups/perDiem';
import { profileRoutes } from './groups/profile';
import { projectManagementRoutes } from './groups/projectManagement';
import { projectRoutes } from './groups/projects';
import { remittanceRoutes } from './groups/remittance';
import { reportRoutes } from './groups/reports';
import { roleRoutes } from './groups/roles';
import { stateRoutes } from './groups/states';
import { structureVersionRoutes } from './groups/structureVersions';
import { systemRoutes } from './groups/systemEnv';
import { taskRoutes } from './groups/tasks';
import { timesheetsRoutes } from './groups/timesheets';
import { userRoutes } from './groups/users';
import { velocityForecastRoutes } from './groups/velocityForecast';
import { workforceOldRoutes, workforceRoutes } from './groups/workforce';
// Lazy load root page (login/landing)
const HomePage = lazy(() => import('@/pages/HomePage'));
const CopSharePage = lazy(() => import('@/pages/share/[shareUrl]/[folderId]/index'));
const CopShareLayout = lazy(() => import('@/pages/share/[shareUrl]/layout'));
const DriveSharePage = lazy(() => import('@/pages/share/drive/[sharingUrl]/[folderId]/index'));
const DriveShareLayout = lazy(() => import('@/pages/share/drive/[sharingUrl]/layout'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
/**
 * Main route configuration for the application
 * Combines all route groups and special routes
 */
export const routes: RouteConfig[] = [
  // Root page (login/landing) - bypasses dashboard layout
  {
    path: '/',
    element: HomePage,
    noRootLayout: true, // Bypasses all layouts for login page
  },
  // Public COP folder share - no login required
  {
    path: '/share/:shareUrl/:folderId',
    element: CopSharePage,
    layout: CopShareLayout,
  },
  // Public Drive share - no login required
  {
    path: '/share/drive/:sharingUrl/:folderId?',
    element: DriveSharePage,
    layout: DriveShareLayout,
  },

  // Access-denied / not-found landing. AccessProvider sends every routeAccess
  // denial here; without a real route the catch-all in AppRouter bounced it to
  // '/', which is the login page, so a missing permission looked like a logout
  // (UG-4757).
  {
    path: '/404',
    element: NotFoundPage,
    noRootLayout: true,
  },

  // Dashboard and all authenticated routes
  dashboardRoutes,
  clientDashboardRoutes,
  activitiesRoutes,
  checkInRoutes,
  gigsRoutes,
  importRoutes,
  jobSpecForecastRoutes,
  notificationsRoutes,
  readinessForecastRoutes,
  restorationsRoutes,
  revenueReportRoutes,
  sessionsRoutes,
  alertRoutes,
  clientRoutes,
  licenseRoutes,
  companyRoutes,
  divisionRoutes,
  structureVersionRoutes,
  hierarchyRoutes,
  stateRoutes,
  emergencyRoutes,
  invoiceRoutes,
  marketRoutes,
  perDiemRoutes,
  timesheetsRoutes,
  profileRoutes,
  projectRoutes,
  projectManagementRoutes,
  remittanceRoutes,
  reportRoutes,
  roleRoutes,
  ...taskRoutes,
  hrbsRoutes,
  userRoutes,
  velocityForecastRoutes,
  workforceRoutes,
  workforceOldRoutes,
  systemRoutes,
  damageRoutes,
  copRoutes,
  forecastsPLRoutes,
  gpmDriveRoutes,
];
